import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Bug, Lightbulb } from 'lucide-react'

import { ApiError } from '@/api/client'
import { feedbackApi } from '@/api/endpoints'
import type { FeedbackKind } from '@/api/types'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'
import { zbierzKontekst } from '@/lib/diagnostyka'
import type { KontekstZgloszenia } from '@/lib/diagnostyka'

const RODZAJE: { value: FeedbackKind; label: string; opis: string; icon: typeof Bug }[] = [
  {
    value: 'BUG',
    label: 'Błąd',
    opis: 'Coś nie działa albo działa inaczej, niż powinno.',
    icon: Bug,
  },
  {
    value: 'SUGGESTION',
    label: 'Sugestia',
    opis: 'Pomysł na usprawnienie albo brakująca funkcja.',
    icon: Lightbulb,
  },
]

interface Props {
  open: boolean
  onClose: () => void
}

/**
 * Okno zgłoszenia błędu lub sugestii.
 *
 * Otwiera się z paska nad każdym ekranem, a nie z osobnej zakładki: błąd
 * zgłasza się tam, gdzie się wydarzył. Dzięki temu adres ekranu, wersja
 * aplikacji, rozmiar okna i ostatnie błędy z konsoli dołączają się same —
 * a to one najczęściej decydują, czy zgłoszenie da się odtworzyć.
 *
 * Kontekst jest pokazany przed wysłaniem. Zbieranie go po cichu byłoby
 * niepoważne wobec ludzi, którzy z tej aplikacji korzystają codziennie.
 */
export function FeedbackModal({ open, onClose }: Props) {
  const queryClient = useQueryClient()
  const { notifySuccess } = useToast()

  const [kind, setKind] = useState<FeedbackKind>('BUG')
  const [message, setMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [kontekst, setKontekst] = useState<KontekstZgloszenia | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setMessage('')
    setKind('BUG')
    // Kontekst zbieramy w chwili otwarcia, nie wysyłki: użytkownik zdąży
    // zmienić rozmiar okna albo wywołać kolejny błąd, zanim dokończy opis.
    setKontekst(zbierzKontekst())
  }, [open])

  const mutation = useMutation({
    mutationFn: () => feedbackApi.create({ kind, message: message.trim(), ...kontekst }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['feedback'] })
      notifySuccess('Dziękujemy, zgłoszenie zostało wysłane.')
      onClose()
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiError ? caught.message : 'Nie udało się wysłać zgłoszenia.')
    },
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    mutation.mutate()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Zgłoś błąd lub sugestię"
      description="Zgłoszenie trafia wprost do serwisu technicznego."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            form="feedback-form"
            isLoading={mutation.isPending}
            disabled={message.trim().length < 3}
          >
            Wyślij zgłoszenie
          </Button>
        </>
      }
    >
      <form id="feedback-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
        {error && <Alert tone="error">{error}</Alert>}

        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-on-surface">Czego dotyczy</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {RODZAJE.map((item) => {
              const Ikona = item.icon
              const wybrany = kind === item.value
              return (
                <label
                  key={item.value}
                  className={cn(
                    'flex cursor-pointer gap-3 rounded-lg border-2 p-3 transition-colors',
                    wybrany
                      ? 'border-primary bg-primary/5'
                      : 'border-transparent bg-surface-container-low hover:bg-surface-container',
                  )}
                >
                  <input
                    type="radio"
                    name="feedback-kind"
                    value={item.value}
                    checked={wybrany}
                    onChange={() => setKind(item.value)}
                    className="mt-1 h-4 w-4 border-outline/40 text-primary focus:ring-primary"
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-bold text-on-surface">
                      <Ikona size={15} aria-hidden="true" />
                      {item.label}
                    </span>
                    <span className="mt-0.5 block text-xs text-on-surface-variant">
                      {item.opis}
                    </span>
                  </span>
                </label>
              )
            })}
          </div>
        </fieldset>

        <div className="space-y-1.5">
          <label htmlFor="feedback-message" className="block text-sm font-semibold text-on-surface">
            Opis
            <span className="ml-1 text-primary" aria-hidden="true">
              *
            </span>
          </label>
          <textarea
            id="feedback-message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            rows={5}
            maxLength={4000}
            autoFocus
            placeholder={
              kind === 'BUG'
                ? 'Co miało się wydarzyć, a co się wydarzyło? Jeśli pamiętasz kolejność kliknięć — napisz ją, to najbardziej pomaga.'
                : 'Co chcesz usprawnić i po co? Powód bywa ważniejszy od samego pomysłu.'
            }
            className="w-full rounded-lg border-2 border-transparent bg-surface-container-low px-4 py-2.5 text-base text-on-surface transition-colors placeholder:text-on-surface-variant/50 focus:border-primary focus:bg-surface focus:outline-none sm:text-sm"
          />
        </div>

        {kontekst && (
          <details className="rounded-lg bg-surface-container-low p-3">
            <summary className="cursor-pointer text-xs font-bold text-on-surface-variant">
              Do zgłoszenia dołączymy dane techniczne
            </summary>
            <dl className="mt-3 space-y-1.5 text-xs text-on-surface-variant">
              <Wiersz etykieta="Ekran" wartosc={kontekst.page_url} />
              <Wiersz etykieta="Wersja" wartosc={kontekst.app_version} />
              <Wiersz etykieta="Okno" wartosc={kontekst.viewport} />
              <Wiersz etykieta="Przeglądarka" wartosc={kontekst.user_agent} />
              <Wiersz
                etykieta="Błędy techniczne"
                wartosc={kontekst.console_errors ?? 'brak'}
                monospace
              />
            </dl>
          </details>
        )}
      </form>
    </Modal>
  )
}

function Wiersz({
  etykieta,
  wartosc,
  monospace,
}: {
  etykieta: string
  wartosc: string
  monospace?: boolean
}) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2">
      <dt className="font-semibold">{etykieta}</dt>
      <dd className={cn('min-w-0 break-words', monospace && 'whitespace-pre-wrap font-mono')}>
        {wartosc}
      </dd>
    </div>
  )
}
